import { motion } from "framer-motion";
import { ArrowRight, HeartHandshake, Users, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";

// Real team photos — shot in our workshop. The first entry is the big hero tile.
const TEAM_PHOTOS = [
  {
    src: "/images/team/team-main.webp",
    alt: "The full Tracksy team at our Australian workshop in front of the Tracksy sign",
    caption: "Pack bench",
  },
  {
    src: "/images/team/photo-3.webp",
    alt: "Tracksy teammate smiling while holding a Tracksy magnetic tracker",
    caption: "Final QC",
  },
  {
    src: "/images/team/photo-4.webp",
    alt: "A Tracksy teammate feeding a treat to the workshop dog",
    caption: "Workshop pup",
  },
  {
    src: "/images/team/photo-1.webp",
    alt: "Two Tracksy teammates on a dispatch video call at the warehouse",
    caption: "Dispatch sync",
  },
  {
    src: "/images/team/photo-5.webp",
    alt: "A Tracksy teammate walking the warehouse aisle with a laptop",
    caption: "Walking the floor",
  },
];

/** One large hero tile + a four-up mosaic on the right. Each photo pops on hover. */
const TeamMosaic = () => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:grid-rows-2 sm:gap-4">
    {/* Hero tile — spans 2x2 on sm+ */}
    <motion.figure
      whileHover={{ scale: 1.015 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="group relative col-span-2 row-span-2 overflow-hidden rounded-[1.75rem] shadow-subtle"
    >
      <img
        src={TEAM_PHOTOS[0].src}
        alt={TEAM_PHOTOS[0].alt}
        loading="lazy"
        className="aspect-[4/5] h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/60 to-transparent" />
      <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-tracksy-orange backdrop-blur">
        <Users className="h-3 w-3" /> The Tracksy team
      </div>
      <figcaption className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-tracksy-ink/85 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur">Design</span>
        <span className="rounded-full bg-tracksy-ink/85 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur">Build</span>
        <span className="rounded-full bg-tracksy-ink/85 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur">Dispatch</span>
        <span className="rounded-full bg-tracksy-ink/85 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur">Support</span>
      </figcaption>
    </motion.figure>
    {/* Four smaller tiles */}
    {TEAM_PHOTOS.slice(1).map((p, i) => (
      <motion.figure
        key={p.src}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="group relative overflow-hidden rounded-2xl shadow-subtle"
      >
        <img
          src={p.src}
          alt={p.alt}
          loading="lazy"
          className="aspect-square h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/55 to-transparent" />
        <figcaption className="absolute bottom-2 left-2 right-2 truncate rounded-full bg-white/90 px-2 py-0.5 text-center text-[9px] font-black uppercase tracking-widest text-tracksy-ink backdrop-blur">
          {p.caption}
        </figcaption>
      </motion.figure>
    ))}
  </div>
);

// Introduces the team on the home page with real workshop photos.
const TeamStrip = () => (
  <section
    data-testid="team-strip-section"
    className="px-4 py-20 sm:px-6 lg:px-8 lg:py-28"
  >
    <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
      {/* Mosaic */}
      <Reveal>
        <TeamMosaic />
      </Reveal>

      {/* Copy */}
      <div>
        <Reveal>
          <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-tracksy-orange/10 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-tracksy-orange">
            <HeartHandshake className="h-3.5 w-3.5" /> Behind every Tracksy
          </p>
        </Reveal>
        <Reveal>
          <h2 className="font-heading text-3xl font-black leading-[1.1] tracking-tight text-tracksy-ink sm:text-4xl lg:text-5xl">
            A small team. One obsession. The thing you can't afford to lose.
          </h2>
        </Reveal>
        <Reveal>
          <p className="mt-5 text-base leading-relaxed text-tracksy-sub sm:text-lg">
            We spent years perfecting Tracksy — from the magnet pull of the
            case to the dispatch label on the box. Every order moves through
            the same four hands: the people who designed it, built it, packed
            it, and reply when you need us. We never stop working with you.
          </p>
        </Reveal>

        <Reveal>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            <li className="rounded-2xl border border-black/5 bg-white p-4 shadow-subtle">
              <div className="flex items-center gap-2 text-tracksy-orange">
                <Wrench className="h-4 w-4" />
                <span className="text-[11px] font-black uppercase tracking-widest">
                  Years of iteration
                </span>
              </div>
              <p className="mt-2 text-sm text-tracksy-ink">
                CAD passes, drop tests, finish samples. Nothing ships until it
                feels obvious.
              </p>
            </li>
            <li className="rounded-2xl border border-black/5 bg-white p-4 shadow-subtle">
              <div className="flex items-center gap-2 text-tracksy-orange">
                <HeartHandshake className="h-4 w-4" />
                <span className="text-[11px] font-black uppercase tracking-widest">
                  Humans, not bots
                </span>
              </div>
              <p className="mt-2 text-sm text-tracksy-ink">
                A real inbox watched by a real person — the same team that
                designed your Tracksy.
              </p>
            </li>
          </ul>
        </Reveal>

        <Reveal>
          <Link
            to="/about"
            data-testid="team-strip-about-link"
            className="mt-8 inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest text-tracksy-orange hover:underline"
          >
            Meet the team <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default TeamStrip;
